import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import PageScaffold from '../components/PageScaffold';
import { authFetch } from '../utils/auth';
import { downloadBlob } from '../utils/caregiverApi';

interface TemplateDetails {
  id: number;
  templateName: string;
  language: string | null;
  formName: string | null;
  signed: boolean;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
}

type EditableTextField = 'templateName' | 'language' | 'formName' | 'userId';

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }
  return data as T;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function TemplateDetail() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [details, setDetails] = useState<TemplateDetails | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [newPdfFile, setNewPdfFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<EditableTextField | null>(null);
  const [editValue, setEditValue] = useState('');
  const pdfUrlRef = useRef<string | null>(null);

  const loadDetails = useCallback(async () => {
    if (!templateId) return;
    const response = await authFetch(`/api/templates/${templateId}/details`);
    const data = await parseJsonResponse<TemplateDetails>(response);
    setDetails(data);
  }, [templateId]);

  const loadPdf = useCallback(async () => {
    if (!templateId) return;
    const response = await authFetch(`/api/templates/${templateId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to load template PDF');
    }
    const blob = await response.blob();
    const nextUrl = URL.createObjectURL(blob);
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    pdfUrlRef.current = nextUrl;
    setPdfUrl(nextUrl);
  }, [templateId]);

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadDetails(), loadPdf()]);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('templates.loadFailedSingle', 'Failed to load template'));
    } finally {
      setLoading(false);
    }
  }, [loadDetails, loadPdf, t]);

  useEffect(() => {
    void loadTemplate();
    return () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    };
  }, [loadTemplate]);

  const startEditing = (field: EditableTextField, currentValue: string | null) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const savePatch = async (patch: Partial<Pick<TemplateDetails, EditableTextField | 'signed'>>) => {
    if (!templateId) return;
    if (patch.templateName !== undefined && !patch.templateName.trim()) {
      setError(t('templates.nameRequired', 'Template name is required.'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/templates/${templateId}/fields`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await parseJsonResponse<{ template: TemplateDetails }>(response);
      setDetails(data.template);
      setEditingField(null);
      setEditValue('');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('templates.updateFailed', 'Failed to update template'));
    } finally {
      setSaving(false);
    }
  };

  const saveField = async () => {
    if (!editingField) return;
    await savePatch({ [editingField]: editValue });
  };

  const handleReplace = async () => {
    if (!templateId || !newPdfFile) {
      setError(t('templates.selectPdfRequired', 'Please select a PDF file.'));
      return;
    }

    const formData = new FormData();
    formData.append('pdfFile', newPdfFile);

    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/templates/${templateId}/replace`, {
        method: 'PUT',
        body: formData,
      });
      await parseJsonResponse(response);
      setShowReplaceDialog(false);
      setNewPdfFile(null);
      await Promise.all([loadDetails(), loadPdf()]);
    } catch (replaceError) {
      setError(replaceError instanceof Error ? replaceError.message : t('templates.replaceFailed', 'Failed to replace template'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!templateId || !details) return;
    setError(null);
    try {
      const response = await authFetch(`/api/templates/${templateId}`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || t('templates.downloadFailed', 'Failed to download template'));
      }
      downloadBlob(await response.blob(), `${details.templateName || `template-${templateId}`}.pdf`);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : t('templates.downloadFailed', 'Failed to download template'));
    }
  };

  const EditableField = ({ label, field, value }: {
    label: string;
    field: EditableTextField;
    value: string | null;
  }) => (
    <Box
      onDoubleClick={() => startEditing(field, value)}
      sx={{ mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
    >
      {editingField === field ? (
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={label}
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          onBlur={() => void saveField()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void saveField();
            if (event.key === 'Escape') {
              setEditingField(null);
              setEditValue('');
            }
          }}
          disabled={saving}
        />
      ) : (
        <Typography variant="body1">
          <strong>{label}:</strong> {value || t('N/A', 'N/A')}
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!details) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error">{error || t('templates.notFound', 'Template not found')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/templates')} sx={{ mt: 2 }}>
          {t('templates.backToTemplates', 'Back to Templates')}
        </Button>
      </Container>
    );
  }

  return (
    <PageScaffold
      title={details.templateName || t('templates.untitled', 'Untitled Template')}
      subtitle={`${t('templates.created', 'Created')}: ${formatDate(details.createdAt)}`}
      actions={(
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/templates')}>
            {t('templates.backToTemplates', 'Back to Templates')}
          </Button>
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setShowReplaceDialog(true)}>
            {t('templates.replacePdf', 'Replace PDF')}
          </Button>
          <Button variant="contained" onClick={() => void handleDownload()}>
            {t('documents.downloadPdf', 'Download PDF')}
          </Button>
        </Stack>
      )}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Dialog open={showReplaceDialog} onClose={() => setShowReplaceDialog(false)}>
        <DialogTitle>{t('templates.replacePdfTemplate', 'Replace PDF Template')}</DialogTitle>
        <DialogContent>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ mt: 2 }}>
            {newPdfFile ? newPdfFile.name : t('templates.selectPdf', 'Select PDF')}
            <input
              hidden
              type="file"
              accept="application/pdf,.pdf"
              onChange={(event) => setNewPdfFile(event.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReplaceDialog(false)} disabled={saving}>{t('Cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={() => void handleReplace()} disabled={saving}>
            {saving ? t('Uploading', 'Uploading...') : t('Upload', 'Upload')}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2, alignItems: 'start' }}>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <EditableField label={t('templates.templateName', 'Template Name')} field="templateName" value={details.templateName} />
          <EditableField label={t('templates.formName', 'Form Name')} field="formName" value={details.formName} />
          <EditableField label={t('Language', 'Language')} field="language" value={details.language} />
          <EditableField label={t('templates.userId', 'User ID')} field="userId" value={details.userId} />
          <FormControlLabel
            control={(
              <Checkbox
                checked={details.signed}
                onChange={(event) => void savePatch({ signed: event.target.checked })}
                disabled={saving}
              />
            )}
            label={t('templates.signed', 'Signed')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('templates.doubleClickEdit', 'Double-click any text field to edit')}
          </Typography>
        </Box>

        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50', minHeight: 600, p: 1 }}>
          {pdfUrl ? (
            <Box
              component="iframe"
              src={pdfUrl}
              title={`${details.templateName} PDF`}
              sx={{ width: '100%', height: '80vh', border: 0, bgcolor: 'white', borderRadius: 1 }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
    </PageScaffold>
  );
}
